import ExcelJS from "exceljs";
import { isValidNumber, plainTitle } from "./quickViewMapper";

/**
 * Palette mirroring the on-screen Quick View table, as ARGB hex for ExcelJS.
 */
const COLORS = {
  headerFill: "FF3F88A5",
  headerFont: "FFFFFFFF",
  moduleFill: "FFE6F2F6",
  moduleFont: "FF1F3A44",
  valueFill: "FFECFDF3",
  valueFont: "FF067647",
  emptyFill: "FFFEF3F2",
  emptyFont: "FFB42318",
  questionFont: "FF111827",
  zebraFill: "FFF9FAFB",
  border: "FFE5E7EB",
};

const THIN_BORDER = {
  top: { style: "thin", color: { argb: COLORS.border } },
  left: { style: "thin", color: { argb: COLORS.border } },
  bottom: { style: "thin", color: { argb: COLORS.border } },
  right: { style: "thin", color: { argb: COLORS.border } },
};

/**
 * Excel display formats for value cells (Indian-style digit grouping to mirror
 * the on-screen en-IN grouping). Integers use a format with NO decimal section
 * so no trailing "." can ever render; values with decimals show up to two
 * places with trailing zeros dropped. Chosen per cell based on the value. The
 * cell always holds a real number; these only control rendering.
 */
const NUMBER_FORMAT_INTEGER = "#,##,##0";
const NUMBER_FORMAT_DECIMAL = "#,##,##0.##";

/**
 * Truncates a number to two decimals WITHOUT rounding, returned as a Number so
 * the stored cell value stays summable/sortable in Excel while displaying the
 * same figure as the on-screen table (which also truncates). String-based to
 * avoid the float error of Math.trunc(n * 100) / 100 (e.g. 0.29 -> 0.28).
 *
 * @param {number|string} value - A numeric value.
 * @returns {number} The value truncated to two decimal places.
 */
const truncateToTwo = (value) => {
  const num = Number(value);
  const negative = num < 0;
  const [intDigits, decDigits = ""] = Math.abs(num).toString().split(".");
  const magnitude = Number(`${intDigits}.${decDigits.slice(0, 2) || "0"}`);
  return negative ? -magnitude : magnitude;
};

/**
 * Sanitises a string into a legal Excel sheet name (max 31 chars, and none
 * of : \ / ? * [ ]).
 *
 * @param {string} name - Desired sheet name.
 * @returns {string} A safe sheet name, never empty.
 */
const sheetName = (name) =>
  String(name || "Sheet").replace(/[:\\/?*[\]]/g, " ").trim().slice(0, 31) || "Sheet";

/**
 * Returns a legal Excel sheet name (≤31 chars) guaranteed unique within
 * `usedNames`, following Excel's own convention of appending an incrementing
 * " (n)" suffix on collision. The base is truncated so `base + suffix` still
 * fits the 31-char cap — otherwise the slice would strip the suffix and
 * reproduce the colliding name, spinning the de-dup loop forever. Adds the
 * chosen name to `usedNames` and returns it.
 *
 * @param {string} label - Desired sheet name.
 * @param {Set<string>} usedNames - Names already taken; mutated in place.
 * @returns {string} A safe, unique sheet name.
 */
const uniqueSheetName = (label, usedNames) => {
  const base = sheetName(label);
  if (!usedNames.has(base)) {
    usedNames.add(base);
    return base;
  }
  let counter = 2;
  let candidate;
  do {
    const suffix = ` (${counter})`;
    candidate = `${base.slice(0, 31 - suffix.length).trim()}${suffix}`;
    counter += 1;
  } while (usedNames.has(candidate));
  usedNames.add(candidate);
  return candidate;
};

/**
 * Applies a solid fill to a cell.
 */
const fill = (cell, argb) => {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
};

/**
 * Fills one styled worksheet: a frozen header, a coloured band per module and
 * one row per question. Answered numeric cells hold the raw number (green,
 * rendered via the integer/decimal number formats) so the export stays
 * summable/sortable/chartable; unanswered cells are left blank (red, with a
 * "No answer" hover note) rather than a misleading 0. Module bands are coloured
 * per-cell rather than merged, so value columns remain unbroken ranges that
 * Excel can sort, filter and auto-sum. The unit is appended to the question
 * text so value columns stay clean.
 *
 * @param {Object} worksheet - An ExcelJS worksheet.
 * @param {Object<string, Array<Object>>} groupedModules - Module -> questions.
 * @param {Array<{key: string, label: string}>} periods - Period columns.
 * @param {Object<string, Object>} quickViewMap - questionId -> period -> cell.
 * @param {Object<string, string>} [unitSettingsMap={}] - questionId -> unit fallback.
 * @returns {void}
 */
const populateSheet = (worksheet, groupedModules, periods, quickViewMap, unitSettingsMap = {}) => {
  const totalCols = periods.length + 1;

  worksheet.columns = [
    { width: 54 },
    ...periods.map(() => ({ width: 16 })),
  ];

  const headerRow = worksheet.addRow([
    "Question",
    ...periods.map((p) => p.label),
  ]);
  headerRow.height = 26;
  headerRow.eachCell((cell, col) => {
    fill(cell, COLORS.headerFill);
    cell.font = { bold: true, color: { argb: COLORS.headerFont }, size: 11 };
    cell.alignment = {
      vertical: "middle",
      horizontal: col === 1 ? "left" : "center",
      wrapText: true,
    };
    cell.border = THIN_BORDER;
  });

  worksheet.views = [{ state: "frozen", xSplit: 1, ySplit: 1 }];

  Object.keys(groupedModules).forEach((moduleName) => {
    const questions = groupedModules[moduleName];

    const bandRow = worksheet.addRow([`🏢  ${moduleName}   (${questions.length})`]);
    bandRow.height = 22;
    const bandCell = worksheet.getCell(bandRow.number, 1);
    bandCell.font = { bold: true, color: { argb: COLORS.moduleFont }, size: 11 };
    bandCell.alignment = { vertical: "middle", horizontal: "left" };
    for (let col = 1; col <= totalCols; col += 1) {
      const cell = worksheet.getCell(bandRow.number, col);
      fill(cell, COLORS.moduleFill);
      cell.border = THIN_BORDER;
    }

    questions.forEach((question, index) => {
      const answers = quickViewMap[question.questionId] || {};
      const unit =
        Object.values(answers).find((cell) => cell?.unit)?.unit ||
        unitSettingsMap[question.questionId];

      const dataRow = worksheet.addRow([
        plainTitle(question.title) + (unit ? ` (${unit})` : ""),
      ]);

      const questionCell = dataRow.getCell(1);
      questionCell.font = { color: { argb: COLORS.questionFont }, bold: true, size: 10.5 };
      questionCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      if (index % 2 === 1) fill(questionCell, COLORS.zebraFill);
      questionCell.border = THIN_BORDER;

      periods.forEach((p, i) => {
        const cell = dataRow.getCell(i + 2);
        const raw = answers[p.key]?.value;
        const numeric = isValidNumber(raw);
        const answered = numeric || (raw != null && raw !== "");

        if (numeric) {
          const truncated = truncateToTwo(raw);
          cell.value = truncated;
          cell.numFmt = Number.isInteger(truncated)
            ? NUMBER_FORMAT_INTEGER
            : NUMBER_FORMAT_DECIMAL;
        } else if (answered) {
          cell.value = String(raw);
        } else {
          cell.note = "No answer";
        }

        fill(cell, answered ? COLORS.valueFill : COLORS.emptyFill);
        cell.font = {
          color: { argb: answered ? COLORS.valueFont : COLORS.emptyFont },
          italic: !answered,
          bold: answered,
          size: 10.5,
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = THIN_BORDER;
      });
    });
  });
};

/**
 * Triggers a browser download of an ExcelJS workbook.
 *
 * @param {Object} workbook - An ExcelJS workbook.
 * @param {string} filename - Download file name.
 * @returns {Promise<void>}
 */
const downloadWorkbook = async (workbook, filename) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/**
 * Downloads a single-sheet workbook with values summed across every selected
 * location, matching the on-screen table.
 *
 * @param {Object} params
 * @param {Object<string, Array<Object>>} params.groupedModules - Module -> questions.
 * @param {Array<{key: string, label: string}>} params.periods - Period columns.
 * @param {Object<string, Object>} params.quickViewMap - questionId -> period -> cell.
 * @param {string} params.financialYearValue - e.g. "2026-2027".
 * @param {Object<string, string>} [params.unitSettingsMap={}] - questionId -> unit fallback.
 * @returns {Promise<void>} Triggers a browser download.
 */
export const downloadQuickViewExcel = async ({
  groupedModules,
  periods,
  quickViewMap,
  financialYearValue,
  unitSettingsMap = {},
}) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(
    sheetName(financialYearValue ? `FY ${financialYearValue}` : "Quick View")
  );
  populateSheet(worksheet, groupedModules, periods, quickViewMap, unitSettingsMap);
  await downloadWorkbook(workbook, `quick_view_${financialYearValue || "export"}.xlsx`);
};

/**
 * Downloads one workbook containing a separate sheet per location (or selected
 * sub-location), each holding only that unit's values. Sheets whose names would
 * collide (e.g. same-named sub-locations) are disambiguated via uniqueSheetName.
 *
 * @param {Object} params
 * @param {Object<string, Array<Object>>} params.groupedModules - Module -> questions.
 * @param {Array<{key: string, label: string}>} params.periods - Period columns.
 * @param {Array<{label: string, quickViewMap: Object}>} params.locationMaps - One
 *   entry per sheet, with that unit's own render map.
 * @param {string} params.financialYearValue - e.g. "2026-2027".
 * @param {Object<string, string>} [params.unitSettingsMap={}] - questionId -> unit fallback.
 * @returns {Promise<void>} Triggers a browser download.
 */
export const downloadQuickViewExcelPerLocation = async ({
  groupedModules,
  periods,
  locationMaps,
  financialYearValue,
  unitSettingsMap = {},
}) => {
  const workbook = new ExcelJS.Workbook();
  const usedNames = new Set();

  locationMaps.forEach(({ label, quickViewMap }) => {
    const worksheet = workbook.addWorksheet(uniqueSheetName(label, usedNames));
    populateSheet(worksheet, groupedModules, periods, quickViewMap, unitSettingsMap);
  });

  await downloadWorkbook(workbook, `quick_view_${financialYearValue || "export"}_by_location.xlsx`);
};
